import { createFileRoute, Link } from "@tanstack/react-router";
import { absUrl } from "@/lib/site-config";
import {
  ArrowRight,
  BadgeCheck,
  Scale,
  ShieldCheck,
  MapPin,
  Phone,
  Mail,
  ExternalLink,
  CheckCircle2,
} from "lucide-react";
import { COMPANY } from "@/content/company";
import { callLink, mailtoLink, whatsappLink } from "@/lib/lead";
import earthlinkLogoUrl from "@/assets/earthlink-logo.png";
import stonebridgeLogoUrl from "@/assets/stonebridge-logo.png";

export const Route = createFileRoute("/partners/stonebridge")({
  head: () => ({
    meta: [
      { title: "UK Visa Services for Clients | Stonebridge" },
      {
        name: "description",
        content:
          "UK visa advice and applications for clients are delivered only by Stonebridge under a separate contract; Earthlink provides no UK immigration advice.",
      },
      { property: "og:title", content: "UK Visa Services for Clients - Stonebridge" },
      {
        property: "og:description",
        content:
          "All UK visa advice and application processing through Stonebridge. 5.0/158 reviews.",
      },
      { property: "og:url", content: absUrl("/partners/stonebridge") },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "UK Visa Services for Clients - Stonebridge" },
      {
        name: "twitter:description",
        content:
          "All UK visa advice and application processing through Stonebridge. 5.0/158 reviews.",
      },
    ],
    links: [{ rel: "canonical", href: absUrl("/partners/stonebridge") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": absUrl("/partners/stonebridge#uk-visa-service"),
          name: "UK visa advice and application processing for clients",
          serviceType: "UK visa advice and application processing",
          url: absUrl("/partners/stonebridge"),
          areaServed: { "@type": "Country", name: "United Kingdom" },
          provider: {
            "@type": "Organization",
            name: COMPANY.solicitors.name,
            url: "https://stonebridgelegal.co.uk/",
          },
          termsOfService:
            "Earthlink does not provide UK immigration advice or prepare UK visa applications. Stonebridge provides all UK visa advice and application processing under a separate contract.",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: { "@id": absUrl("/") },
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "UK visa services by Stonebridge",
            },
          ],
        }),
      },
    ],
  }),
  component: StonebridgePartnerPage,
});

const PARTNER_SERVICES = [
  {
    title: "Student & Graduate Visas",
    blurb:
      "Stonebridge handles the visa stage of your study journey, including advice and application processing.",
    items: [
      "Student Visa (Sponsored Study route)",
      "Child Student Visa",
      "Graduate Visa (2 years through 31 December 2026; 18 months from 1 January 2027; 3 years for doctoral graduates)",
      "Extensions and switching routes",
    ],
  },
  {
    title: "Family & Partner Visas",
    blurb:
      "For students bringing a spouse, partner, or children — and for families joining a settled relative.",
    items: [
      "Spouse Visa",
      "Unmarried Partner Visa",
      "Dependent Visa (PBS routes)",
      "Fiancé(e) Visa",
    ],
  },
  {
    title: "Skilled Worker & Health Care",
    blurb:
      "UK visa advice and applications after a jobseeker independently receives an eligible offer.",
    items: [
      "Skilled Worker Visa",
      "Health and Care Worker Visa",
      "Graduate-to-Skilled-Worker switching advice",
      "Route eligibility and application advice",
    ],
  },
  {
    title: "Business, Innovator & Mobility",
    blurb: "UK visa advice and application processing for eligible business and mobility routes.",
    items: [
      "Innovator Founder Visa",
      "UK Expansion Worker Visa",
      "Graduate Trainee & Specialist Worker Visa",
      "High Potential Individual Visa",
    ],
  },
  {
    title: "Visit & Short-Stay Visas",
    blurb: "For parents attending your graduation, conferences, or short academic visits.",
    items: [
      "Standard Visitor Visa",
      "Business Visitor Visa",
      "Academic Visitor Visa",
      "Parent of a Child Student Visa",
    ],
  },
];

const WORKFLOW = [
  {
    n: "01",
    t: "You start with Earthlink",
    b: "Earthlink handles counselling, university shortlisting, and university applications within each institution's rules. Earthlink does not advise on UK immigration.",
  },
  {
    n: "02",
    t: "We refer the UK visa work",
    b: "Every UK visa route, advice matter, form, and application is referred to Stonebridge Legal Solutions in writing.",
  },
  {
    n: "03",
    t: "Stonebridge advises you directly",
    b: "You sign a separate service agreement with them. They quote their own fees and own the UK visa advice and application work.",
  },
  {
    n: "04",
    t: "We coordinate, you stay informed",
    b: "Earthlink can coordinate non-visa service timing with your Stonebridge handover only where you request and authorise the necessary update. Each contract remains separate.",
  },
];

const FAQS = [
  {
    q: "Who handles UK visa advice and applications?",
    a: "Stonebridge alone handles advice and application processing for every UK visa route presented through Earthlink. Earthlink's work is limited to its separately identified non-visa services.",
  },
  {
    q: "Do I pay Earthlink and Stonebridge separately?",
    a: "Yes. Earthlink and Stonebridge provide separate contracts for their respective scopes. Each party must state its own fees, cancellation and refund terms, and complaints route before work begins.",
  },
  {
    q: "Is Earthlink itself a law firm?",
    a: "No. Earthlink is an education consultancy and does not provide UK visa advice, document assessment, form preparation, or application processing.",
  },
  {
    q: "Who decides a UK visa application?",
    a: "The relevant UK authority makes every visa decision. Neither Earthlink nor Stonebridge can guarantee an outcome.",
  },
  {
    q: "What if I already have a solicitor?",
    a: "You may choose an adviser independently, but Earthlink will not provide UK immigration advice or application work. UK visa services presented through Earthlink are delivered only by Stonebridge.",
  },
];

function StonebridgePartnerPage() {
  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-muted text-foreground">
        <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_30%_20%,_color-mix(in_oklab,_var(--color-brand)_40%,_transparent),_transparent_50%),radial-gradient(circle_at_75%_80%,_color-mix(in_oklab,_var(--color-gold)_30%,_transparent),_transparent_45%)]" />
        <div className="container-page relative py-20 md:py-28">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Partner firms</p>
          <img
            src={stonebridgeLogoUrl}
            alt="Stonebridge Legal Solutions logo"
            className="mx-auto mt-4 h-20 w-auto md:h-24"
            loading="eager"
          />
          <h1 className="mt-6 max-w-3xl font-serif text-4xl text-balance leading-tight md:text-6xl">
            UK visa services for clients — delivered by{" "}
            <span className="text-brand">Stonebridge</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-foreground/85">
            Earthlink handles your education journey. Stonebridge alone handles every UK immigration
            route, advice matter, form, and application — including student, visitor, work, family,
            business, and appeal matters.
          </p>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {[
              { icon: BadgeCheck, label: "All UK visa routes", sub: "Advice and applications" },
              { icon: Scale, label: "5.0 / 158 reviews", sub: "Existing published figures" },
              { icon: ShieldCheck, label: "Separate contract", sub: "Clear service scope" },
            ].map((s) => (
              <div
                key={s.label}
                className="flex items-center gap-3 rounded-md border border-border bg-paper/5 p-4 backdrop-blur"
              >
                <s.icon className="h-6 w-6 text-gold" />
                <div>
                  <div className="font-semibold">{s.label}</div>
                  <div className="text-sm text-muted-foreground">{s.sub}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <a
              href={whatsappLink(
                { message: "I'd like to be referred to Stonebridge for immigration advice." },
                "Partner page",
              )}
              className="btn-primary rounded-sm px-5 py-3 text-sm font-medium"
            >
              Request a referral <ArrowRight className="ml-2 inline h-4 w-4" />
            </a>
            <Link
              to="/contact"
              className="rounded-sm border border-border px-5 py-3 text-sm font-medium text-foreground transition hover:bg-background/50"
            >
              Book a free consultation
            </Link>
          </div>
        </div>
      </section>

      {/* Who they are */}
      <section className="container-page py-20">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-brand">About the firm</p>
            <h2 className="mt-3 font-serif text-3xl text-navy md:text-4xl">
              Stonebridge handles every UK visa route
            </h2>
            <p className="mt-5 text-muted-foreground">
              Stonebridge alone provides the advice and application processing for UK student,
              visitor, work, family, dependant, extension, switching, and other UK visa routes
              presented through Earthlink.
            </p>
            <p className="mt-4 text-muted-foreground">
              The existing published figures of 5.0 stars across 158 reviews are unchanged.
              Stonebridge's engagement, fees, cancellation and refund responsibility, and complaints
              route remain separate from Earthlink's non-visa services.
            </p>

            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                "All UK visa advice and applications",
                "Student, visitor, work, and family routes",
                "Extensions and switching routes",
                "Separate service contract",
                "Separate fees and refund responsibility",
                "Separate complaints route",
              ].map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-navy">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  {p}
                </li>
              ))}
            </ul>
          </div>

          <aside className="rounded-md border hairline bg-card p-6">
            <img
              src={stonebridgeLogoUrl}
              alt="Stonebridge Legal Solutions"
              className="mb-5 h-16 w-auto"
              loading="lazy"
            />
            <h3 className="font-serif text-xl text-navy">Stonebridge</h3>
            <div className="mt-5 space-y-4 text-sm">
              <a
                href="https://stonebridgelegal.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-navy transition hover:text-brand"
              >
                <ExternalLink className="h-4 w-4 text-brand" /> stonebridgelegal.co.uk
              </a>
            </div>

            <div className="my-6 h-px bg-border" />

            <img
              src={earthlinkLogoUrl}
              alt="Earthlink Consultants logo"
              className="mb-3 h-12 w-auto"
              loading="lazy"
            />
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Earthlink Consultants
            </p>
            <div className="mt-4 space-y-4 text-sm">
              <div className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                <div className="text-navy">
                  {COMPANY.address.line1}, {COMPANY.address.line2}
                  <br />
                  {COMPANY.address.city} {COMPANY.address.postcode}
                </div>
              </div>
              <a
                href={callLink()}
                className="flex items-center gap-3 text-navy transition hover:text-brand"
              >
                <Phone className="h-4 w-4 text-brand" /> {COMPANY.phone}
              </a>
              <a
                href={mailtoLink()}
                className="flex items-center gap-3 text-navy transition hover:text-brand"
              >
                <Mail className="h-4 w-4 text-brand" /> {COMPANY.email}
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* Joint services */}
      <section className="bg-secondary/40 py-20">
        <div className="container-page">
          <p className="text-xs uppercase tracking-[0.25em] text-brand">Services in partnership</p>
          <h2 className="mt-3 max-w-3xl font-serif text-3xl text-navy md:text-4xl">
            What we deliver together
          </h2>
          <p className="mt-5 max-w-2xl text-muted-foreground">
            Earthlink owns the non-visa education side — universities, offers, documents, finances,
            and pre-departure support. Stonebridge owns every UK visa advice and application step.
          </p>

          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {PARTNER_SERVICES.map((s) => (
              <article
                key={s.title}
                className="group rounded-md border hairline bg-card p-6 transition hover:-translate-y-1 hover:border-brand hover:shadow-lg"
              >
                <h3 className="font-serif text-xl text-navy">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.blurb}</p>
                <ul className="mt-4 space-y-2 text-sm">
                  {s.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-navy">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand transition group-hover:text-gold" />
                      {it}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="container-page py-20">
        <p className="text-xs uppercase tracking-[0.25em] text-brand">How the handover works</p>
        <h2 className="mt-3 max-w-3xl font-serif text-3xl text-navy md:text-4xl">
          One journey. Two firms. Clear lines.
        </h2>

        <ol className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {WORKFLOW.map((w) => (
            <li key={w.n} className="relative rounded-md border hairline bg-card p-6">
              <div className="font-serif text-3xl text-brand">{w.n}</div>
              <h3 className="mt-3 font-semibold text-navy">{w.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{w.b}</p>
            </li>
          ))}
        </ol>

        <div className="mt-10 rounded-md border-l-4 border-brand bg-secondary/50 p-5 text-sm text-navy">
          <strong className="text-navy-deep">Plain English:</strong> Earthlink is your education
          consultancy. Stonebridge is your UK visa service provider. The engagement letters, fees,
          cancellation and refund responsibility, complaints routes, and service responsibility
          remain separate.
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-muted py-20 text-foreground">
        <div className="container-page">
          <p className="text-xs uppercase tracking-[0.25em] text-gold">Common questions</p>
          <h2 className="mt-3 max-w-3xl font-serif text-3xl md:text-4xl">About the partnership</h2>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {FAQS.map((f) => (
              <div key={f.q} className="rounded-md border border-border bg-paper/5 p-6">
                <h3 className="font-semibold text-foreground">{f.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container-page py-20">
        <div className="rounded-md bg-secondary p-10 text-center text-foreground md:p-16">
          <h2 className="font-serif text-3xl md:text-4xl">Ready for a clearly scoped plan?</h2>
          <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
            Start with a free 30-minute call with Earthlink. If your case needs UK visa advice or
            application processing, we'll introduce you to Stonebridge; Earthlink will not advise on
            or submit the UK visa application.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              href={whatsappLink(
                { message: "I'd like to discuss a study + visa case that may need Stonebridge." },
                "Partner page",
              )}
              className="btn-primary rounded-sm px-6 py-3 text-sm font-medium"
            >
              WhatsApp Earthlink
            </a>
            <Link
              to="/contact"
              className="rounded-sm border border-border px-6 py-3 text-sm font-medium transition hover:bg-background/50"
            >
              Book a consultation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
